import { useEffect, useState, type ReactNode } from "react";
import ClientOperatorNav from "@/components/ClientOperatorNav";
import type { Client } from "@/pages/api/clients";

const bg = "#09090d";
const border = "#28283a";
const muted = "#8d8da7";
const faint = "#626278";
const text = "#ececf5";

function host(value: string): string {
  try { return new URL(value.startsWith("http") ? value : `https://${value}`).hostname.replace(/^www\./, ""); }
  catch { return value.replace(/^https?:\/\//, "").replace(/^www\./, "").split("/")[0]; }
}

export default function SeoClientWorkspaceShell({ clientId, children }: { clientId: string; children: ReactNode }) {
  const [client, setClient] = useState<Client | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetch("/api/clients", { cache: "no-store" })
      .then(response => response.json())
      .then(body => {
        if (cancelled || !Array.isArray(body?.clients)) return;
        setClient((body.clients as Client[]).find(item => item.id === clientId) || null);
      })
      .catch(() => {
        // The existing SEO surface remains usable even if the portfolio header cannot resolve.
      });
    return () => { cancelled = true; };
  }, [clientId]);

  return <div className="blackbird-client-seo-shell" style={{ minHeight: "100vh", background: bg, color: text, fontFamily: "Inter, ui-sans-serif, system-ui, sans-serif" }}>
    <div style={{ maxWidth: 1420, margin: "0 auto", padding: "22px 18px 0" }}>
      <ClientOperatorNav clientId={clientId} active="seo">
        <a href="/clients" style={{ border: `1px solid ${border}`, background: "#15151d", color: "#d8d8e3", borderRadius: 9, padding: "8px 11px", fontSize: 10, fontWeight: 800, textDecoration: "none" }}>Change client</a>
      </ClientOperatorNav>

      <header style={{ display: "flex", justifyContent: "space-between", gap: 14, alignItems: "flex-end", flexWrap: "wrap", marginBottom: 10 }}>
        <div>
          <div style={{ color: "#8da0ff", fontSize: 9, fontWeight: 900, textTransform: "uppercase", letterSpacing: ".13em" }}>Client SEO workspace</div>
          <h1 style={{ margin: "5px 0 3px", fontSize: 25, letterSpacing: "-.03em" }}>{client?.name || "SEO"}</h1>
          <div style={{ color: faint, fontSize: 10 }}>{client?.baseDomain ? host(client.baseDomain) : "Selected client context"}</div>
        </div>
        <div style={{ color: muted, fontSize: 10 }}>Client stays selected across every SEO step.</div>
      </header>
    </div>

    <style jsx global>{`
      .blackbird-client-seo-shell .embedded-seo main {
        min-height: 0 !important;
        padding-top: 0 !important;
      }
      .blackbird-client-seo-shell .embedded-seo main > div {
        max-width: 1420px !important;
      }
      .blackbird-client-seo-shell .embedded-seo main > div > header:first-of-type,
      .blackbird-client-seo-shell .embedded-seo main > div > section:first-of-type {
        display: none !important;
      }
    `}</style>
    <div className="embedded-seo">{children}</div>
  </div>;
}
